import { useState, useEffect } from 'react';
import { makeDropDownMask } from '@/utilities/dropdownHelpers';

type SelectOption = { value: string; label: string };
type OptionGroup = { optgroup: string; options: SelectOption[] };
type MakeApiItem = { makeCode?: string; makeDescription?: string };

const DEFAULT_MAKE_OPTIONS: (SelectOption | OptionGroup)[] = [{ value: '', label: 'Any Make' }];

/**
 * Custom hook for managing make dropdown selection and fetching
 * Handles make selection changes and populates make options
 */
export const useMakeDropdown = () => {
  const [selectedMake, setSelectedMake] = useState<string>('');
  const [makeOptions, setMakeOptions] = useState<(SelectOption | OptionGroup)[]>(DEFAULT_MAKE_OPTIONS);
  const [isLoadingMakes, setIsLoadingMakes] = useState<boolean>(true);

  // Fetch vehicle makes on component mount
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        setIsLoadingMakes(true);
        const response = await fetch('/api/psx-buy/vehicle-makes');
        const data: MakeApiItem[] = await response.json();

        if (Array.isArray(data)) {
          const maskedOptions = makeDropDownMask(data);
          setMakeOptions(maskedOptions);
        }
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setMakeOptions(DEFAULT_MAKE_OPTIONS);
      } finally {
        setIsLoadingMakes(false);
      }
    };

    fetchMakes();
  }, []);

  /**
   * Handle make selection change
   */
  const handleMakeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMakeCode = event.target.value;
    setSelectedMake(selectedMakeCode);
    // Component will handle model fetching via separate effect
  };

  return {
    selectedMake,
    setSelectedMake,
    makeOptions,
    setMakeOptions,
    handleMakeChange,
    isLoadingMakes,
  };
};
